import { Colors, Img, Lightning, Registry } from "@lightningjs/sdk";
import { Icon } from "../../../../components/data-display/Icon/Icon";
import { MediaPoster } from "../../../../components/data-display/MediaPoster/MediaPoster";
import { TranslatableText } from "../../../../components/data-display/TranslatableText/TranslatableText";
import { ChannelItemModel, ProgramModel } from "../../../../lib/models";
import theme from "../../../../lib/theme";

const HINT_ICON_SIZE = 30;

const PAD_X = 45;
const PAD_Y = 34;
const LOGO_W = 182;
const CONTENT_H = 145;
const HINTS_W = 558;
const HINTS_X = theme.layout.w - HINTS_W - PAD_X;
const PROGRESS_H = theme.channelItem.progressBar.h;

// Normal mode
const INFO_X = PAD_X + LOGO_W + 24;
const INFO_W = theme.layout.w - INFO_X - PAD_X;

export const BANNER_H = 214;

// Preview mode — taller banner, 16:9 image on left
const PREVIEW_W = 648; // 364 * 16/9
const PREVIEW_H = 364;
export const BANNER_H_PREVIEW = PREVIEW_H + PAD_Y * 2;
// Logo/Info/LogoRight row is bottom-aligned within the taller preview banner
const CONTENT_Y_PREVIEW = BANNER_H_PREVIEW - BANNER_H + PAD_Y;
const INFO_X_PREVIEW = PAD_X + PREVIEW_W + 24;
const HINTS_W_PREVIEW = 558;
const HINTS_X_PREVIEW = theme.layout.w - HINTS_W_PREVIEW - PAD_X;
// LogoRight: right-aligned, same bottom row as Info
const LOGO_RIGHT_X = theme.layout.w - LOGO_W - PAD_X;
const INFO_W_PREVIEW = theme.layout.w - INFO_X_PREVIEW - 24 - LOGO_W - PAD_X;
const HINT_COL_W = 240;
const HINT_GAP = 20;
const PLACEHOLDER_LOGO_W = 200;
const PLACEHOLDER_LOGO_H = Math.round((PLACEHOLDER_LOGO_W * 9) / 16);

interface NowPlayingBannerSpec extends Lightning.Component.TemplateSpec {
    Background: object;
    Preview: {
        Bg: object;
        Placeholder: {
            Logo: typeof MediaPoster;
        };
        Image: object;
    };
    Logo: {
        Bg: object;
        Poster: typeof MediaPoster;
    };
    Info: {
        Top: {
            ChannelName: object;
            ProgramName: object;
        };
        Bottom: {
            Time: object;
            ProgressBar: {
                Progress: object;
            };
        };
    };
    LogoRight: {
        Bg: object;
        Poster: typeof MediaPoster;
    };
    Hints: {
        Row1: {
            NavIcon: typeof Icon;
            NavIconDivider: object;
            NextChannel: typeof TranslatableText;
            NavPreviewIcon: typeof Icon;
            NavPreviewIconDivider: object;
            NextChannelPreview: typeof TranslatableText;
            InfoIcon: typeof Icon;
        };
        Row2: {
            Select: typeof TranslatableText;
            OK: {
                Text: typeof TranslatableText;
            };
        };
    };
}

export class NowPlayingBanner
    extends Lightning.Component<NowPlayingBannerSpec>
    implements Lightning.Component.ImplementTemplateSpec<NowPlayingBannerSpec>
{
    private _channel: ChannelItemModel | null = null;
    private _updateInterval: number | null = null;
    private _infoW = INFO_W;
    private _previewTxHandler!: () => void;
    private _previewTxErrorHandler!: () => void;
    private _previewRetryCount = 0;
    private _previewUrl: string | null = null;
    private _previewRetryTimeout: number | null = null;

    static override _template(): Lightning.Component.Template<NowPlayingBannerSpec> {
        return {
            w: theme.layout.w,
            h: BANNER_H,
            Background: {
                w: (w) => w,
                h: (h) => h,
                rect: true,
                color: Colors(theme.colors.bg.primary).alpha(0.85).get()
            },
            // Preview image — left side, preview mode only
            Preview: {
                x: PAD_X,
                y: PAD_Y,
                w: PREVIEW_W,
                h: CONTENT_H,
                alpha: 0,
                transitions: { alpha: { duration: 0.2 } },
                Bg: {
                    w: (w) => w,
                    h: (h) => h,
                    rect: true,
                    color: Colors(theme.colors.bg.light).alpha(0.9).get(),
                    shader: {
                        type: Lightning.shaders.RoundedRectangle,
                        radius: theme.channelItem.bg.border.radius
                    }
                },
                Placeholder: {
                    w: (w) => w,
                    h: (h) => h,
                    Logo: {
                        type: MediaPoster,
                        useBorder: false,
                        mount: 0.5,
                        x: PREVIEW_W / 2,
                        y: PREVIEW_H / 2,
                        w: PLACEHOLDER_LOGO_W,
                        h: PLACEHOLDER_LOGO_H
                    }
                },
                Image: {
                    w: (w) => w,
                    h: (h) => h,
                    alpha: 0.01,
                    transitions: { alpha: { duration: 0.3 } }
                }
            },
            // Channel logo — left side in normal mode
            Logo: {
                x: PAD_X,
                y: PAD_Y,
                w: LOGO_W,
                h: CONTENT_H,
                Bg: {
                    w: (w) => w,
                    h: (h) => h,
                    rect: true,
                    color: Colors(theme.colors.bg.light).alpha(0.9).get(),
                    shader: {
                        type: Lightning.shaders.RoundedRectangle,
                        radius: theme.channelItem.bg.border.radius
                    }
                },
                Poster: {
                    x: (LOGO_W - theme.channelItem.logo.w) / 2,
                    y: (CONTENT_H - theme.channelItem.logo.h) / 2,
                    w: theme.channelItem.logo.w,
                    h: theme.channelItem.logo.h,
                    type: MediaPoster,
                    useBorder: false
                }
            },
            Info: {
                x: INFO_X,
                y: PAD_Y,
                w: INFO_W,
                h: CONTENT_H,
                flex: { direction: "column" },
                Top: {
                    flexItem: { grow: 1 },
                    flex: { direction: "column" },
                    x: 0,
                    // y: TOP_Y,
                    w: (w) => w,
                    ChannelName: {
                        y: 2,
                        text: {
                            text: "",
                            fontSize: 36,
                            textColor: Colors(theme.colors.text.primary).get(),
                            maxLines: 1,
                            textOverflow: "ellipsis",
                            wordWrapWidth: INFO_W
                        }
                    },
                    ProgramName: {
                        // y: PROGRAM_Y,
                        y: -2,
                        text: {
                            text: "",
                            fontSize: 20,
                            textColor: Colors(theme.colors.text.primary).alpha(0.7).get(),
                            maxLines: 2,
                            textOverflow: "ellipsis",
                            wordWrapWidth: INFO_W
                        }
                    }
                },
                Bottom: {
                    flex: { direction: "column" },
                    x: 0,
                    // y: BOTTOM_Y,
                    w: (w) => w,
                    Time: {
                        y: -4,
                        text: {
                            text: "",
                            fontSize: 20,
                            textColor: Colors(theme.colors.text.secondary).get()
                        }
                    },
                    ProgressBar: {
                        y: -2,
                        // y: PROGRESS_Y,
                        w: (w) => w,
                        h: PROGRESS_H,
                        rect: true,
                        color: Colors(theme.colors.text.primary).alpha(0.4).get(),
                        shader: { type: Lightning.shaders.RoundedRectangle, radius: 3 },
                        Progress: {
                            x: 0,
                            y: 0,
                            w: 0,
                            h: PROGRESS_H,
                            rect: true,
                            color: Colors(theme.colors.text.primary).alpha(0.8).get(),
                            shader: { type: Lightning.shaders.RoundedRectangle, radius: 3 }
                        }
                    }
                }
            },
            // Channel logo — right side in preview mode
            LogoRight: {
                x: LOGO_RIGHT_X,
                y: PAD_Y,
                w: LOGO_W,
                h: CONTENT_H,
                alpha: 0,
                Bg: {
                    w: (w) => w,
                    h: (h) => h,
                    rect: true,
                    color: Colors(theme.colors.bg.light).alpha(0.9).get(),
                    shader: {
                        type: Lightning.shaders.RoundedRectangle,
                        radius: theme.channelItem.bg.border.radius
                    }
                },
                Poster: {
                    x: (LOGO_W - theme.channelItem.logo.w) / 2,
                    y: (CONTENT_H - theme.channelItem.logo.h) / 2,
                    w: theme.channelItem.logo.w,
                    h: theme.channelItem.logo.h,
                    type: MediaPoster,
                    useBorder: false
                }
            },
            Hints: {
                x: HINTS_X,
                y: PAD_Y,
                w: HINTS_W,
                flex: { direction: "column" },
                Row1: {
                    w: HINTS_W,
                    h: 59,
                    flex: { direction: "row", justifyContent: "center" },
                    texture: Lightning.Tools.getRoundRect(
                        HINTS_W,
                        59,
                        17,
                        1,
                        Colors(theme.colors.text.primary).alpha(0.25).get(),
                        true,
                        Colors(theme.colors.text.primary).alpha(0.15).get(),
                    ),
                    NextChannel: {
                        y: 2,
                        type: TranslatableText,
                        key: "livetv.nextChannel",
                        flexItem: { marginRight: 15, alignSelf: "center" },
                        text: {
                            fontFace: "Regular",
                            fontSize: 22,
                            textColor: Colors(theme.colors.text.primary).alpha(0.75).get(),
                            wordWrapWidth: HINT_COL_W
                        }
                    },
                    NavIcon: {
                        type: Icon,
                        w: HINT_ICON_SIZE,
                        h: HINT_ICON_SIZE,
                        icon: "remote-left-right",
                        iconWidth: HINT_ICON_SIZE,
                        hasActive: false,
                        flexItem: { marginRight: 15, alignSelf: "center" },
                    },
                    NavIconDivider: {
                        w: 1,
                        h: 47,
                        rect: true,
                        color: Colors(theme.colors.text.primary).alpha(0.3).get(),
                        flexItem: { marginRight: 15, alignSelf: "center" }
                    },
                    NextChannelPreview: {
                        y: 2,
                        type: TranslatableText,
                        key: "livetv.nextChannelPreview",
                        flexItem: { marginRight: 10, alignSelf: "center" },
                        text: {
                            fontFace: "Regular",
                            fontSize: 22,
                            textColor: Colors(theme.colors.text.primary).alpha(0.75).get(),
                            wordWrapWidth: HINT_COL_W
                        }
                    },
                    NavPreviewIcon: {
                        type: Icon,
                        w: HINT_ICON_SIZE,
                        h: HINT_ICON_SIZE,
                        icon: "remote-up-down",
                        iconWidth: HINT_ICON_SIZE,
                        hasActive: false,
                        flexItem: { marginRight: 15, alignSelf: "center" }
                    },
                    NavPreviewIconDivider: {
                        w: 1,
                        h: 47,
                        rect: true,
                        color: Colors(theme.colors.text.primary).alpha(0.3).get(),
                        flexItem: { marginRight: 15, alignSelf: "center" }
                    },
                    InfoIcon: {
                        type: Icon,
                        w: HINT_ICON_SIZE,
                        h: HINT_ICON_SIZE,
                        icon: "info",
                        iconWidth: HINT_ICON_SIZE,
                        hasActive: false,
                        flexItem: { alignSelf: "center" }
                    }
                },
                Row2: {
                    y: 20,
                    w: HINTS_W,
                    h: 40,
                    alpha: 0,
                    flex: { direction: "row", justifyContent: "flex-end" },
                    Select: {
                        y: (h) => h / 2 + 2,
                        mountY: 0.5,
                        type: TranslatableText,
                        key: "livetv.select",
                        text: {
                            fontFace: "Regular",
                            fontSize: 22,
                            textColor: Colors(theme.colors.text.primary).alpha(0.75).get(),
                            wordWrapWidth: HINT_COL_W
                        }
                    },
                    OK: {
                        flexItem: { marginLeft: HINT_GAP },
                        texture: Lightning.Tools.getRoundRect(
                            63,
                            40,
                            6,
                            1,
                            Colors(theme.colors.text.primary).alpha(0.75).get(),
                            true,
                            Colors(theme.colors.bg.primary).alpha(0.55).get()
                        ),
                        Text: {
                            y: (h) => h / 2 + 2,
                            x: (h) => h / 2,
                            mount: 0.5,
                            type: TranslatableText,
                            key: "livetv.ok",
                            text: {
                                fontFace: "Regular",
                                fontSize: 22,
                                textColor: Colors(theme.colors.text.primary).alpha(0.75).get(),
                                wordWrapWidth: HINT_COL_W,
                                textAlign: "center"
                            }
                        }
                    }
                }
            }
        };
    }

    override _setup() {
        this._previewTxHandler = this._onPreviewLoaded.bind(this);
        this._previewTxErrorHandler = this._onPreviewError.bind(this);
        const image = (this.getByRef("Preview") as any).tag("Image");
        image.on("txLoaded", this._previewTxHandler);
        image.on("txError", this._previewTxErrorHandler);
    }

    override _unfocus() {
        this._cancelRetry();
    }

    private _cancelRetry() {
        if (this._previewRetryTimeout !== null) {
            Registry.clearTimeout(this._previewRetryTimeout);
            this._previewRetryTimeout = null;
        }
        this._previewUrl = null;
        this._previewRetryCount = 0;
    }

    private _onPreviewLoaded() {
        const preview = this.getByRef("Preview") as any;
        preview.tag("Image").setSmooth("alpha", 1);
        preview.tag("Placeholder").setSmooth("alpha", 0);
        this._previewRetryCount = 0;
    }

    private _onPreviewError() {
        const url = this._previewUrl;
        if (this._previewRetryCount < 100 && url) {
            this._previewRetryCount++;
            this._previewRetryTimeout = Registry.setTimeout(() => {
                this._previewRetryTimeout = null;
                if (this._previewUrl !== url) return;
                const image = (this.getByRef("Preview") as any).tag("Image");
                // Cache-bust the URL so Lightning creates a fresh TextureSource
                // and actually re-fetches from the network instead of reusing
                // the cached error state from the previous attempt.
                image.texture = Img(`${url}&_t=${Date.now()}`).contain(PREVIEW_W, PREVIEW_H);
            }, 1000);
        }
    }

    // Normal mode: channel currently playing
    set item(channel: ChannelItemModel) {
        this._channel = channel;
        this._applyNormalMode();
        this._updateInfo();
        this._startProgressUpdate();
    }

    // Preview mode: user browsing without playing
    set previewItem(channel: ChannelItemModel) {
        this._channel = channel;
        this._applyPreviewMode();
        this._updateInfo();
        this._startProgressUpdate();
    }

    private _applyNormalMode() {
        this._cancelRetry();
        this._infoW = INFO_W;
        this.patch({
            Logo: { smooth: { alpha: 1 }, y: PAD_Y },
            Preview: { smooth: { alpha: 0 }, y: PAD_Y, h: CONTENT_H },
            LogoRight: { smooth: { alpha: 0 }, y: PAD_Y },
            Info: {
                x: INFO_X,
                y: PAD_Y,
                w: INFO_W,
                Top: {
                    ChannelName: { text: { wordWrapWidth: INFO_W } },
                    ProgramName: { text: { wordWrapWidth: INFO_W } }
                }
            },
            Hints: {
                x: HINTS_X,
                y: PAD_Y,
                w: HINTS_W,
                Row1: { w: HINTS_W },
                Row2: { w: HINTS_W, smooth: { alpha: 0 } }
            }
        });
    }

    private _applyPreviewMode() {
        this._infoW = INFO_W_PREVIEW;
        const tvgId = this._channel?.tvg_id;

        const preview = this.getByRef("Preview") as any;
        const image = preview.tag("Image");
        const logo = preview.tag("Placeholder.Logo");

        // Cancel any pending retry and reset state
        this._cancelRetry();

        // Show placeholder, hide image
        image.alpha = 0.01;
        image.texture = null;
        preview.tag("Placeholder").alpha = 1;
        logo.data = this._channel;

        // Load preview image and fade in on success
        if (tvgId) {
            const url = `https://livetv2.alteox.stream/${tvgId}/preview.jpg?height=${PREVIEW_H}&`;
            this._previewUrl = url;
            image.texture = Img(url).contain(PREVIEW_W, PREVIEW_H);
        }

        this.patch({
            Logo: { smooth: { alpha: 0 }, y: CONTENT_Y_PREVIEW },
            Preview: {
                smooth: { alpha: 1 },
                y: PAD_Y,
                h: PREVIEW_H
            },
            LogoRight: { smooth: { alpha: 1 }, x: LOGO_RIGHT_X, y: CONTENT_Y_PREVIEW },
            Info: {
                x: INFO_X_PREVIEW,
                y: CONTENT_Y_PREVIEW,
                w: INFO_W_PREVIEW,
                Top: {
                    ChannelName: { text: { wordWrapWidth: INFO_W_PREVIEW } },
                    ProgramName: { text: { wordWrapWidth: INFO_W_PREVIEW } }
                }
            },
            Hints: {
                x: HINTS_X_PREVIEW,
                y: PAD_Y,
                w: HINTS_W_PREVIEW,
                Row1: { w: HINTS_W_PREVIEW },
                Row2: { w: HINTS_W_PREVIEW, smooth: { alpha: 1 } }
            }
        });
    }

    private _updateInfo() {
        if (!this._channel) return;
        const now = new Date();
        const currentProgram = this._getCurrentProgram(now);
        const programTime = currentProgram
            ? `${this._formatTime(currentProgram.start)} - ${this._formatTime(currentProgram.stop)}`
            : "";

        this.patch({
            Logo: { Poster: { data: this._channel } },
            LogoRight: { Poster: { data: this._channel } },
            Info: {
                Top: {
                    ChannelName: { text: { text: this._channel.name ?? "" } },
                    ProgramName: { text: { text: currentProgram?.title ?? "" } }
                },
                Bottom: {
                    Time: { text: { text: programTime } }
                }
            }
        });
        this._updateProgress(currentProgram, now);
    }

    private _updateProgress(currentProgram?: ProgramModel | null, now: Date = new Date()) {
        if (!currentProgram) {
            this.patch({ Info: { Bottom: { ProgressBar: { Progress: { w: 0 } } } } });
            return;
        }
        const start = new Date(currentProgram.start);
        const end = new Date(currentProgram.stop);
        const total = end.getTime() - start.getTime();
        const elapsed = now.getTime() - start.getTime();
        const progress = Math.min(1, Math.max(0, elapsed / total));
        this.patch({ Info: { Bottom: { ProgressBar: { Progress: { w: this._infoW * progress } } } } });
    }

    private _getCurrentProgram(now: Date): ProgramModel | undefined {
        return this._channel?.programs?.find((p) => now >= new Date(p.start) && now < new Date(p.stop));
    }

    private _formatTime(dateString: string): string {
        return new Date(dateString).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    }

    private _startProgressUpdate() {
        this._stopProgressUpdate();
        this._updateInterval = setInterval(() => {
            if (this._channel) this._updateInfo();
        }, 30000) as unknown as number;
    }

    private _stopProgressUpdate() {
        if (this._updateInterval) {
            clearInterval(this._updateInterval);
            this._updateInterval = null;
        }
    }

    override _handleEnter() {
        if (this._channel) {
            this.fireAncestors("$nowPlayingBannerSelect", this._channel);
        }
    }

    override _detach() {
        this._stopProgressUpdate();
        this._cancelRetry();
        const image = (this.getByRef("Preview") as any)?.tag("Image");
        if (image) {
            image.off("txLoaded", this._previewTxHandler);
            image.off("txError", this._previewTxErrorHandler);
        }
    }
}
